import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  GridDirection,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
  withStyles,
  useMediaQuery,
} from '@material-ui/core'
import classNames from 'classnames'
import { FC, Fragment } from 'react'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { closeDialog, openDialog } from '../../actions/dialogs'
import { CloseDialog, OpenDialog } from '../../actions/types'
import { TitleWithCloseButton } from '../../components/Messages'
import { selectDialogVisibility } from '../../selectors/dialogs'
import i18n from '../../translations/i18n'
import { MuiClasses } from '../../types'
import { ButtonColor } from '../../types/components'

interface OpenDialogButtonProps {
  classes: MuiClasses
  buttonLabel: string
  buttonClassName: any
  color: 'inherit' | 'default' | 'primary' | 'secondary' | undefined
  size: 'small' | 'medium' | 'large' | undefined
  dialogId: string
  buttonFullWidth: boolean
  ButtonIcon: any
  open: OpenDialog
  tooltipTitle: string
  disabled?: boolean
  t: i18n.TFunction
}
const styles = () => ({
  root: { overflow: 'visible' },
  primaryBtn: {
    backgroundColor: '#94004A',
    '&:hover': {
      backgroundColor: '#670033'
    }
  }
})

const OpenDialogButton: FC<OpenDialogButtonProps> = ({
  classes,
  buttonLabel,
  buttonClassName,
  color,
  size,
  dialogId,
  buttonFullWidth,
  ButtonIcon,
  open,
  tooltipTitle,
  disabled,
  t
}) => {
  return (
    <Fragment>
      {buttonLabel ? (
        <Button
          className={buttonClassName}
          variant='contained'
          color={color || 'default'}
          size={size || 'small'}
          disabled={disabled}
          fullWidth={buttonFullWidth || false}
          onClick={() => open(dialogId)}
        >
          {ButtonIcon && <ButtonIcon style={{ marginRight: 6 }} />}
          {buttonLabel}
        </Button>
      ) : (
        <Fragment>
          {tooltipTitle ? (
            <Tooltip
              className={buttonClassName || null}
              title={`${t(tooltipTitle)}`}
            >
              <IconButton
                onClick={() => open(dialogId)}
                aria-label='dialogButtonIcon'
              >
                <ButtonIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              onClick={() => open(dialogId)}
              aria-label='dialogButtonIcon'
            >
              <ButtonIcon />
            </IconButton>
          )}
        </Fragment>
      )}
    </Fragment>
  )
}

interface OpenDialogMenuItemButtonProps {
  buttonLabel: string
  buttonClassName: any
  dialogId: string
  ButtonIcon: any
  open: OpenDialog
}

const OpenDialogMenuItemButton: FC<OpenDialogMenuItemButtonProps> = ({
  buttonLabel,
  buttonClassName,
  dialogId,
  ButtonIcon,
  open
}) => (
  <MenuItem onClick={() => open(dialogId)} className={buttonClassName}>
    <ListItemIcon>
      <ButtonIcon />
    </ListItemIcon>
    <ListItemText primary={buttonLabel} />
  </MenuItem>
)

const renderButton = (
  isMenuItem: any,
  menuItemButtonProps: any,
  buttonProps: any,
  classes: MuiClasses
): any =>
  isMenuItem ? (
    <OpenDialogMenuItemButton {...menuItemButtonProps} />
  ) : (
    <OpenDialogButton classes={classes} {...buttonProps} />
  )

const renderConfirmDialogActions = (
  closeDialogAction: (id: string) => void,
  confirmDialogSubmitAction: any,
  beforeCloseDialogAction: any,
  dialogId: string,
  confirmLabel?: string,
  closeLabel?: string,
  confirmBtnColor?: ButtonColor,
  onlyConfirmCloses?: boolean,
  customButtonContainerDirection?: GridDirection,
  customButtonContainerClassName?: any,
  customButtonConfirmClassName?: any,
  customButtonCloseClassName?: any,
  buttonContainerStyles?: string,
  agreeButtonDisabled?: boolean,
  hideDialog?: boolean
) => (
  <DialogActions>
    <Grid
      container
      justify='space-between'
      direction={customButtonContainerDirection || 'row'}
      className={buttonContainerStyles}
    >
      <Grid
        item
        className={
          customButtonContainerClassName ? customButtonContainerClassName : ''
        }
      >
        {!onlyConfirmCloses && (
          <Button
            className={
              customButtonCloseClassName ? customButtonCloseClassName : ''
            }
            onClick={() => {
              if (beforeCloseDialogAction) {
                beforeCloseDialogAction()
              }
              closeDialogAction(dialogId)
            }}
            color='primary'
          >
            {closeLabel ? closeLabel : i18n.t('general.cancel')}
          </Button>
        )}
      </Grid>
      <Grid
        item
        className={
          customButtonContainerClassName ? customButtonContainerClassName : ''
        }
      >
        <Button
          className={
            customButtonConfirmClassName ? customButtonConfirmClassName : ''
          }
          onClick={() => {
            if (confirmDialogSubmitAction) {
              confirmDialogSubmitAction()
            }
            closeDialogAction(dialogId)
          }}
          disabled={agreeButtonDisabled}
          color={confirmBtnColor ? confirmBtnColor : 'primary'}
          autoFocus
        >
          {confirmLabel ? confirmLabel : i18n.t('general.agree')}
        </Button>
      </Grid>
    </Grid>
  </DialogActions>
)

const renderOnlyDialogCloseAction = (
  closeDialogAction: (id: string) => void,
  dialogId: string
) => (
  <DialogActions>
    <Grid container justify='space-between'>
      <Grid item>
        <Button onClick={() => closeDialogAction(dialogId)} color='primary'>
          {i18n.t('general.close')}
        </Button>
      </Grid>
    </Grid>
  </DialogActions>
)
interface DialogContainerProps {
  open: OpenDialog
  close: CloseDialog
  t: i18n.TFunction
  dialogVisibility: any
  dialogId: string
  title: string
  buttonLabel: string
  Content: any
  ButtonIcon: any
  disabled?: boolean
  color: 'inherit' | 'default' | 'primary' | 'secondary' | undefined
  size: 'small' | 'medium' | 'large' | undefined
  buttonClassName: any
  buttonFullWidth: boolean
  tooltipTitle: string
  maxWidth: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | undefined
  disableScroll: boolean
  classes: MuiClasses
  optionalTitleText: string
  translateTitle: boolean
  isMenuItem: boolean
  noOpenDialogButton: boolean
  isConfirmDialog: boolean
  confirmDialogSubmitAction: any
  beforeCloseDialogAction: any
  confirmLabel?: string
  closeLabel?: string
  confirmColor?: ButtonColor
  onlyCloseButton?: boolean
  onlyConfirmCloses?: boolean
  defaultsToVisible?: boolean
  borderRadius?: string
  customCloseIcon?: any
  customButtonContainerDirection?: GridDirection
  customButtonContainerClassName?: any
  customButtonConfirmClassName?: any
  customButtonCloseClassName?: any
  buttonContainerStyles?: string
  noFreeSpace?: boolean
  hideCloseIcon?: boolean
  fullScreenDialogMobile?: boolean,
  agreeButtonDisabled?: false
  hideDialog?: false
  onClose?: () => void;
}

const DialogContainer: FC<DialogContainerProps> = ({
  open,
  close,
  dialogVisibility,
  dialogId,
  title,
  size,
  buttonLabel,
  Content,
  ButtonIcon,
  disabled,
  color,
  buttonClassName,
  buttonFullWidth,
  tooltipTitle,
  maxWidth,
  paperMaxWidth,
  disableScroll,
  classes,
  optionalTitleText,
  translateTitle,
  isMenuItem,
  t,
  noOpenDialogButton,
  confirmDialogSubmitAction,
  beforeCloseDialogAction,
  confirmLabel,
  closeLabel,
  confirmColor,
  onlyCloseButton,
  customCloseIcon,
  onlyConfirmCloses,
  defaultsToVisible,
  borderRadius,
  customButtonContainerDirection,
  customButtonContainerClassName,
  customButtonConfirmClassName,
  customButtonCloseClassName,
  buttonContainerStyles,
  noFreeSpace,
  hideCloseIcon,
  fullScreenDialogMobile = false,
  agreeButtonDisabled,
  hideDialog,
  onClose
}) => {
  const isDialogVisible = hideDialog
    ? false // If hideDialog is true, force the dialog to be hidden
    : defaultsToVisible
      ? /** allows dialog to default to visible, but if dialog is closed through an action, it should hide */
      true ||
      !(
        dialogVisibility.id === dialogId &&
        dialogVisibility?.isVisible === 'false'
      )
      : dialogVisibility.id === dialogId && dialogVisibility.isVisible;

  const menuItemButtonProps = {
    buttonLabel,
    buttonClassName,
    dialogId,
    ButtonIcon,
    open
  }
  const buttonProps = {
    buttonLabel,
    buttonClassName,
    color,
    size,
    dialogId,
    buttonFullWidth,
    ButtonIcon,
    open,
    tooltipTitle,
    disabled,
    t
  }

  const isMobileWidth = useMediaQuery('(max-width:600px)')
  const isDialogFullScreen = fullScreenDialogMobile && isMobileWidth

  // dialog overflow issue fix https://github.com/mui-org/material-ui/issues/7431
  const dialogClasses = disableScroll ? { paperScrollPaper: classes.root } : {}
  const titleText = optionalTitleText
    ? `${t(title)} ${t(optionalTitleText)}`
    : translateTitle
      ? `${t(title)}`
      : title
  return (
    <Fragment>
      {noOpenDialogButton ? (
        <Fragment />
      ) : (
        renderButton(isMenuItem, menuItemButtonProps, buttonProps, classes)
      )}
      <Dialog
        fullWidth
        maxWidth={maxWidth}
        PaperProps={{
          style: {
            borderRadius: borderRadius,
            ...(paperMaxWidth ? { maxWidth: paperMaxWidth } : {}),
            ...(isDialogFullScreen && {
              margin: '0px',
              width: '100%',
              height: '100%',
              maxHeight: 'none',
            }),
          },
        }}
        open={isDialogVisible}
        onClose={() => {
          if (onClose) {
            onClose();
          }
          close(dialogId);
        }}
        aria-labelledby='dialog-title'
        classes={dialogClasses}
        disableBackdropClick={onlyConfirmCloses}
        disableEscapeKeyDown={onlyConfirmCloses}
        disableEnforceFocus
        disableAutoFocus
      >
        {
          <TitleWithCloseButton
            title={titleText}
            closeAction={close}
            actionParams={dialogId}
            t={t}
            customCloseIcon={customCloseIcon}
            hideCloseIcon={hideCloseIcon || onlyConfirmCloses}
            onClose={onClose}
          />
        }
        <DialogContent
          style={{ padding: '0px 24px' }}
          className={classNames(classes.root, disableScroll)}
        >
          {Content}
          {!noFreeSpace && <div style={{ padding: '10px 0px 25px' }} />}
          {(confirmDialogSubmitAction || beforeCloseDialogAction) &&
            renderConfirmDialogActions(
              close,
              confirmDialogSubmitAction,
              beforeCloseDialogAction,
              dialogId,
              confirmLabel,
              closeLabel,
              confirmColor,
              onlyConfirmCloses,
              customButtonContainerDirection,
              customButtonContainerClassName,
              customButtonConfirmClassName,
              customButtonCloseClassName,
              buttonContainerStyles,
              agreeButtonDisabled
            )}
          {onlyCloseButton && renderOnlyDialogCloseAction(close, dialogId)}
        </DialogContent>
      </Dialog>
    </Fragment>
  )
}

const mapStateToProps = (state: { [key: string]: any }) => ({
  dialogVisibility: selectDialogVisibility(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  close: (dialogId: string) => dispatch(closeDialog(dialogId)),
  open: (dialogId: string) => dispatch(openDialog(dialogId))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withStyles(styles)(DialogContainer) as any)) as any
